import { navigate } from "gatsby";
import debounce from "lodash.debounce";
import React, { useMemo } from "react";
import {
	type PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { THANK_YOU_STATUS } from "./fixtures";
import { usePersistSurveyData } from "./hooks/usePersistSurveyData";
import { getNextScreen } from "./routing";

const SOFT_END_SCREENS: NDS.ScreenName[] = ["Q32", "END_OF_SURVEY"];

export const multiDogFields: NDS.FormFieldName[] = [
	"nds_q_3_dog_age_years_",
	"nds_q_3_dog_age_months_",
	"nds_q_4_dog_sex_neuter_",
	"nds_q_6_breed_type_",
	"nds_q_6_1_single_breed_",
	"nds_q_6_1_single_breed_unlisted_",
	"nds_q_6_2_first_crossbreed_",
	"nds_q_6_2_first_crossbreed_unlisted_",
	"nds_q_6_3_second_crossbreed_",
	"nds_q_6_3_second_crossbreed_unlisted_",
	"nds_q_6_3_dog_size_",
	"nds_q_8_acquisition_date_month_",
	"nds_q_8_acquisition_date_year_",
	"nds_q_9_source_",
	"nds_q_9_source_other_",
	"nds_q_10_how_found_",
	"nds_q_10_how_found_other_",
	"nds_q_10_how_found_site_",
	"nds_q_12_dog_traits_confidence_",
	"nds_q_12_dog_traits_excitability_",
	"nds_q_12_dog_traits_manners_",
	"nds_q_12_dog_traits_training_",
	"nds_q_12_dog_traits_strangers_",
	"nds_q_12_dog_traits_dogs_",
	"nds_q_12_dog_traits_adaptability_",
	"nds_q_12_dog_traits_playfulness_",
	"nds_q_12_dog_traits_energy_",
	"nds_q_12_dog_traits_attachment_",
	"nds_q_12_dog_traits_barking_",
	"nds_q_12_dog_traits_biddability_",
	"nds_q_12_dog_traits_grooming_",
	"nds_q_12_dog_traits_food_",
	"nds_q_12_dog_traits_treats_",
	"nds_q_12_dog_traits_travel_",
	"nds_q_12_dog_traits_water_",
	"nds_q_12_dog_traits_care_",
	"nds_q_12_dog_traits_living_",
	"nds_q_12_dog_traits_health_",
	"nds_q_15_behaviour_",
	"nds_q_15_behaviour_other_",
];

interface NationalDogSurveyContextState {
	surveyState: NDS.SurveyState;
	setSurveyState: (
		value: NDS.SurveyState | ((value: NDS.SurveyState) => NDS.SurveyState),
	) => void;
	routeStack: NDS.ScreenState[];
	screenState: NDS.ScreenState;
	disableNext: boolean;
	setDisableNext: (value: boolean) => void;
	formData: NDS.FormData;
	setFormData: (value: NDS.FormData | ((prev: NDS.FormData) => void)) => void;
	onBack?: () => void;
	onNext?: () => void;
	handleChange: (
		name:
			| NDS.FormFieldName
			| Partial<Record<NDS.FormFieldName, string | string[] | null>>,
		value?: string | string[] | null,
	) => void;
	currentDogName?: string;
	onSpecialNavigation: (
		nextRoute: NDS.ScreenIndex,
		nextDogIndex?: number,
	) => void;
	onSubmit: () => void;
	onSoftSubmit: () => Promise<boolean>;
	onLoadWarmRoute: (
		incomingWarmData: NDS.WarmUserData,
		utmMediumParam: string,
		utmSourceParam: string,
	) => void;
	onLoadSupporterId: (supporterId: string) => void;
	transition?: NDS.TransitionTrigger;
	playedTransitions?: NDS.TransitionTrigger[];
	setTransition?: (value: NDS.TransitionTrigger | null) => void;
	setPlayedTransitions?: (value: NDS.TransitionTrigger[]) => void;
	clearSurveyCookies?: () => void;
	importSurveyCookies?: (data: string) => void;
	exportSurveyCookies?: () => string;
	hasInitialisedFromCookies?: boolean;
	questionSchema: NDS.QuestionSchema;
	dogIndex: 1 | 2 | 3 | 4 | 5;
	setQuestionSchema: (value: NDS.QuestionSchema) => void;
	isLoading: boolean;
	handleThankYouReload?: () => void;

	getFieldsForQuestion: (
		question: "Q12" | "Q16" | "Q17" | "Q19" | "Q20" | "Q27" | "E4_1" | "E6",
	) => NDS.FormField[];

	updateFieldsSeen: (fieldName: string) => void;
	// For debugging
	restartSpecificRouteDebug: (
		screenIndex: NDS.ScreenIndex,
		shouldInitialiseDogs?: boolean,
	) => void;
	resetSurvey?: () => void;
	restartWarmRouteDebug: (incomingWarmData: string | null) => void;
	isConfirmMode: "false" | "editing" | "clear";
	setIsConfirmMode: (value: "false" | "editing" | "clear") => void;
	recentFieldChange: {
		screen: NDS.ScreenName;
		fieldNames: Partial<Record<NDS.FormFieldName, string | string[] | null>>;
	};
}
const NationalDogSurveyContext = createContext<NationalDogSurveyContextState>(
	{} as NationalDogSurveyContextState,
);

export const NationalDogSurveyContextProvider: React.FC<PropsWithChildren> = ({
	children,
}) => {
	// The form data submitted by the user
	const [formData, setFormData] = useState<NDS.FormData>({} as NDS.FormData);
	const [fieldsSeen, setFieldsSeen] = useState<string[]>([]);

	const [isLoading, setIsLoading] = useState(false);
	const [hasSubmitted, setHasSubmitted] = useState(false);

	const [transition, setTransition] = useState<NDS.TransitionTrigger | null>(
		null,
	);

	const [disableNext, setDisableNext] = useState<boolean>(false);

	const [_submissionError, setSubmissionError] = useState<string | null>(null);
	const [recentFieldChange, setRecentFieldChange] = useState<{
		screen: NDS.ScreenName;
		fieldNames: Partial<Record<NDS.FormFieldName, string | string[] | null>>;
	}>(null);

	const [questionSchema, setQuestionSchema] =
		useState<NDS.QuestionSchema | null>(null);

	const [isConfirmMode, setIsConfirmMode] = useState<"false" | "editing" | "clear">("false");
	const {
		clearSurveyCookies,
		exportSurveyCookies,
		importSurveyCookies,
		onLoadWarmRoute,
		setSurveyState,
		surveyState,
		hasInitialisedFromCookies,
		// Debugging
		resetSurvey,
		restartSpecificRouteDebug,
		restartWarmRouteDebug,
	} = usePersistSurveyData(
		formData,
		setFormData,
		fieldsSeen,
		setFieldsSeen,
		setTransition,
	);

	/**
	 *  Focuses on the page wrapper for accessibility
	 */
	const focusOnPageWrapper = useCallback(() => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
			document.getElementById("gatsby-focus-wrapper")?.focus();
		}
	}, []);

	/**
	 * Scrolls to the top of the page
	 * */
	const scrollToTop = useCallback(() => {
		window.scrollTo({ top: 0, behavior: "instant" });
	}, []);

	/**
	 *  Goes back to the previous screen
	 */
	const onBack = useCallback(() => {
		const nextRouteStack = surveyState.routeStack.slice(0, -1);

		setSurveyState((prevSurveyState) => ({
			...prevSurveyState,
			routeStack: nextRouteStack,
		}));

		focusOnPageWrapper();
		scrollToTop();
		return;
	}, [scrollToTop, setSurveyState, surveyState.routeStack, focusOnPageWrapper]);

	/**
	 * Proceeds to the next screen
	 * */
	const onNext = useCallback(async () => {
		const {
			surveyState: nextSurveyState,
			formData: nextFormData,
			transition: nextTransition,
		} = getNextScreen(surveyState)(surveyState, formData, questionSchema);

		for (const progressKey of Object.keys(surveyState.surveyProgress)) {
			if (progressKey === "SUBSEQUENT_DOG") {
				continue;
			}
			const key = progressKey as keyof NDS.SurveyProgress;
			nextSurveyState.surveyProgress[key] = Math.max(
				surveyState.surveyProgress[key],
				nextSurveyState.surveyProgress[key],
			);
		}

		const nextScreen =
			nextSurveyState.routeStack[nextSurveyState.routeStack.length - 1].screen;

		if (SOFT_END_SCREENS.includes(nextScreen)) {
			await onSoftSubmit();
		}
		setSurveyState({ ...nextSurveyState });
		setTransition(nextTransition);

		setFormData(nextFormData);

		focusOnPageWrapper();
		scrollToTop();
	}, [scrollToTop, formData, questionSchema, surveyState, setSurveyState, focusOnPageWrapper]);
	const screenState = useMemo(
		() => surveyState.routeStack[surveyState.routeStack.length - 1],
		[surveyState.routeStack[surveyState.routeStack.length - 1]],
	);

	useEffect(() => {
		if (recentFieldChange?.screen !== screenState.screen) {
			setRecentFieldChange(null);
		}
	}, [screenState.screen, recentFieldChange]);

	/**
	 * Handles changes to the form data
	 * */
	const handleChange = useCallback(
		(
			name:
				| NDS.FormFieldName
				| Partial<Record<NDS.FormFieldName, string | string[] | null>>,
			value?: string | string[] | null,
		) => {
			// Handle single field change
			if (typeof name === "string") {
				let fieldName = name;
				if (multiDogFields.includes(name)) {
					// Handle multi dog fields
					const dogIndex = Object.values(screenState.dogsState).find(
						(dog) => dog.isActive,
					)?.index as 1 | 2 | 3 | 4 | 5;

					if (!dogIndex || dogIndex < 1 || dogIndex > 5) {
						console.error("Dog index is out of range");
						return;
					}
					fieldName = `${name}${dogIndex}` as NDS.FormFieldName;
				}
				if (value === undefined) {
					return;
				}

				setFormData((prevFormData) => {
					return { ...prevFormData, [fieldName]: value };
				});

				setRecentFieldChange((prev) => {
					const newFieldNames =
						prev?.screen === screenState.screen
							? { ...prev.fieldNames, [fieldName]: value }
							: { [fieldName]: value };

					return {
						screen: screenState.screen,
						fieldNames: newFieldNames,
					};
				});
			}
			// Handle multiple fields change
			else {
				const fieldsToUpdate = { ...name };
				const recentChanges: Partial<
					Record<NDS.FormFieldName, string | string[] | null>
				> = {};

				// Update formData with multiple fields
				setFormData((prevFormData) => {
					const newFormData = { ...prevFormData };

					for (const [fieldName, fieldValue] of Object.entries(
						fieldsToUpdate,
					)) {
						let finalFieldName = fieldName as NDS.FormFieldName;

						if (multiDogFields.includes(finalFieldName)) {
							// Handle multi dog fields
							const dogIndex = Object.values(screenState.dogsState).find(
								(dog) => dog.isActive,
							)?.index as 1 | 2 | 3 | 4 | 5;

							if (!dogIndex || dogIndex < 1 || dogIndex > 5) {
								console.error(
									"Dog index is out of range for field",
									finalFieldName,
								);
								continue;
							}
							finalFieldName =
								`${finalFieldName}${dogIndex}` as NDS.FormFieldName;
						}

						newFormData[finalFieldName] = fieldValue;
						recentChanges[finalFieldName] = fieldValue;
					}

					return newFormData;
				});

				// Update recentFieldChange using a functional update to prevent race conditions
				setRecentFieldChange((prev) => {
					const newFieldNames =
						prev?.screen === screenState.screen
							? { ...prev.fieldNames, ...recentChanges }
							: { ...recentChanges };

					return {
						screen: screenState.screen,
						fieldNames: newFieldNames,
					};
				});
			}
		},
		[screenState],
	);

	const { executeRecaptcha } = useGoogleReCaptcha();

	/**
	 * Submits the form data
	 * */
	const onSubmit = useCallback(
		async (e?: {
			preventDefault: () => void;
		}) => {
			e?.preventDefault();
			setIsLoading(true);
			return debounce(async () => {
				const { surveyStartTime, warmData, supporterId, surveyResponseId } =
					surveyState;
				const surveyEndTime = Date.now();

				const surveyDuration = surveyEndTime - surveyStartTime;

				// The survey duration in hours:min:sec
				const surveyDurationHours = Math.floor(surveyDuration / 3600000);
				const surveyDurationMinutes = Math.floor(
					(surveyDuration % 3600000) / 60000,
				);
				const surveyDurationSeconds = Math.floor(
					(surveyDuration % 60000) / 1000,
				);

				const formDataToSend = { ...formData };

				// Some values in form data will be "DO_DELETE" we want to remove these before it's sent
				for (const key in formDataToSend) {
					if (
						formDataToSend[key as keyof typeof formDataToSend] === "DO_DELETE"
					) {
						delete formDataToSend[key as keyof typeof formDataToSend];
					}
				}

				for (const fieldName of fieldsSeen) {
					if (!formDataToSend[fieldName as NDS.FormFieldName]) {
						formDataToSend[fieldName as NDS.FormFieldName] = "NAQ";
					}
				}

				const submitData = {
					...formDataToSend,
					surveyStartTime,
					surveyEndTime,
					surveyDuration: `${surveyDurationHours}:${surveyDurationMinutes}:${surveyDurationSeconds}`,
					supporterId: warmData?.supporterId || supporterId,
					responseId: surveyResponseId,
					submitType: "final",
				};

				console.log("Fetching recaptcha token");
				const recaptchaToken = await executeRecaptcha?.("national_dog_survey");
				console.log("Recaptcha token fetched", recaptchaToken);

				submitData.recaptchaToken = recaptchaToken;
				console.log("final", JSON.stringify(submitData, null, 2));

				const submitResponse = await fetch(
					process.env.GATSBY_NDS_SURVEY_SUBMIT_URL,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({ ...submitData }),
					},
				);
				if (submitResponse.ok) {
					setIsLoading(false);
					setHasSubmitted(true);
					setSurveyState((prevSurveyState) => ({
						...prevSurveyState,
						hasSubmitted: true,
						routeStack: [
							...prevSurveyState.routeStack,
							{
								...prevSurveyState.routeStack[
									prevSurveyState.routeStack.length - 1
								],
								screen: "THANK_YOU",
								route: "GLOBAL",
							},
						],
					}));
					handleChange("surveyStatus", "completed");
					navigate("/national-dog-survey/global/nds-thank-you");
				} else {
					console.error(
						"There was an error submitting the form",
						submitResponse,
					);
					setHasSubmitted(false);
					setIsLoading(false);
					setSubmissionError("There was an error submitting the form");
					alert("There was an error submitting the form, please try again");
				}
			}, 5000)();
		},
		[
			formData,
			handleChange,
			executeRecaptcha,
			surveyState,
			fieldsSeen,
			setSurveyState,
		],
	);

	/**
	 * Submits the form data
	 * */
	const onSoftSubmit = useCallback(
		async (e?: {
			preventDefault: () => void;
		}) => {
			e?.preventDefault();
			return debounce(async () => {
				const { surveyStartTime, warmData, supporterId, surveyResponseId } =
					surveyState;
				const surveyEndTime = Date.now();

				const surveyDuration = surveyEndTime - surveyStartTime;

				// The survey duration in hours:min:sec
				const surveyDurationHours = Math.floor(surveyDuration / 3600000);
				const surveyDurationMinutes = Math.floor(
					(surveyDuration % 3600000) / 60000,
				);
				const surveyDurationSeconds = Math.floor(
					(surveyDuration % 60000) / 1000,
				);

				const formDataToSend = { ...formData };

				// Some values in form data will be "DO_DELETE" we want to remove these before it's sent
				for (const key in formDataToSend) {
					if (
						formDataToSend[key as keyof typeof formDataToSend] === "DO_DELETE"
					) {
						delete formDataToSend[key as keyof typeof formDataToSend];
					}
				}

				for (const fieldName of fieldsSeen) {
					if (!formDataToSend[fieldName as NDS.FormFieldName]) {
						formDataToSend[fieldName as NDS.FormFieldName] = "NAQ";
					}
				}

				console.log("Fetching recaptcha token");
				const recaptchaToken = await executeRecaptcha?.("national_dog_survey");
				console.log("Recaptcha token fetched", recaptchaToken);

				const submitData = {
					...formDataToSend,
					surveyStartTime,
					surveyEndTime,
					surveyDuration: `${surveyDurationHours}:${surveyDurationMinutes}:${surveyDurationSeconds}`,
					recaptchaToken,
					responseId: surveyResponseId,
					supporterId: warmData?.supporterId || supporterId,
					submitType: "soft",
				};

				console.log("soft", JSON.stringify(submitData, null, 2));
				const submitResponse = await fetch(
					process.env.GATSBY_NDS_SURVEY_SUBMIT_URL,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify(submitData),
					},
				);

				return submitResponse.ok;
			}, 5000)();
		},
		[formData, surveyState, executeRecaptcha, fieldsSeen],
	);

	const getFieldsForQuestion = useCallback(
		(
			question: "Q12" | "Q16" | "Q17" | "Q19" | "Q20" | "Q27" | "E4_1" | "E6",
		) => {
			if (!questionSchema || !hasInitialisedFromCookies) return [];
			switch (question) {
				// All key question + 3 random
				case "Q12": {
					if (formData.Q12 && Array.isArray(formData.Q12)) {
						const selectedQuestions = formData.Q12.map(
							(i) =>
								questionSchema.UNDERSTANDING_YOUR_DOG.Q12.fields[
									Number.parseInt(i)
								],
						);

						return selectedQuestions;
					}

					const likertScales = questionSchema.UNDERSTANDING_YOUR_DOG.Q12.fields;

					const keyQuestions = likertScales.filter(
						(likertScale) => likertScale.isKeyQuestion,
					);
					const optionalQuestions = likertScales.filter(
						(likertScale) => !likertScale.isKeyQuestion,
					);

					const randomOptionalQuestions = optionalQuestions
						.sort(() => 0.5 - Math.random())
						.slice(0, 3);

					const selectedQuestions = [
						...keyQuestions,
						...randomOptionalQuestions,
					];
					const indexOfSelectedQuestions = selectedQuestions.map((question) =>
						likertScales.indexOf(question),
					);

					setFormData((prevFormData) => ({
						...prevFormData,
						Q12: indexOfSelectedQuestions.map((i) => `${i}`),
					}));

					return [...keyQuestions, ...randomOptionalQuestions];
				}

				// All key questions then four random
				case "Q16": {
					if (formData.Q16 && Array.isArray(formData.Q16)) {
						const selectedQuestions = formData.Q16.map(
							(i) =>
								questionSchema.UNDERSTANDING_YOUR_DOG.Q16.fields[
									Number.parseInt(i)
								],
						);
						return selectedQuestions;
					}
					const likertScales = questionSchema.UNDERSTANDING_YOUR_DOG.Q16.fields;

					const keyQuestions = likertScales.filter(
						(likertScale) => likertScale.isKeyQuestion,
					);
					const optionalQuestions = likertScales.filter(
						(likertScale) => !likertScale.isKeyQuestion,
					);

					const randomOptionalQuestions = optionalQuestions
						.sort(() => 0.5 - Math.random())
						.slice(0, 4);

					const selectedQuestions = [
						...keyQuestions,
						...randomOptionalQuestions,
					];
					const indexOfSelectedQuestions = selectedQuestions.map((question) =>
						likertScales.indexOf(question),
					);

					setFormData((prevFormData) => ({
						...prevFormData,
						Q16: indexOfSelectedQuestions.map((i) => `${i}`),
					}));

					return [...keyQuestions, ...randomOptionalQuestions];
				}
				// 7 statements in total:
				// All key questions
				// Exactly 2 of each category for non-key questions
				case "Q17": {
					if (formData.Q17 && Array.isArray(formData.Q17)) {
						const selectedQuestions = formData.Q17.map(
							(i) =>
								questionSchema.UNDERSTANDING_YOUR_DOG.Q17.fields[
									Number.parseInt(i)
								],
						);
						return selectedQuestions;
					}
					const likertScales = questionSchema.UNDERSTANDING_YOUR_DOG.Q17.fields;

					const keyQuestions = likertScales.filter(
						(likertScale) => likertScale.isKeyQuestion,
					);

					const selectedQuestions = [...keyQuestions];
					const optionalQuestions = likertScales.filter(
						(likertScale) => !likertScale.isKeyQuestion,
					);

					const categories = [
						...new Set(optionalQuestions.map((field) => field.category)),
					];
					for (const filterCategory of categories) {
						const categoryQuestions = optionalQuestions.filter(
							({ category }) => category === filterCategory,
						);

						const randomCategoryQuestions = categoryQuestions
							.sort(() => 0.5 - Math.random())
							.slice(0, 2);

						selectedQuestions.push(...randomCategoryQuestions);
					}

					const indexOfSelectedQuestions = selectedQuestions.map((question) =>
						likertScales.indexOf(question),
					);

					setFormData((prevFormData) => ({
						...prevFormData,
						Q17: indexOfSelectedQuestions.map((i) => `${i}`),
					}));

					return selectedQuestions;
				}

				// Show all key questions then 1 random non key
				case "Q19": {
					if (formData.Q19 && Array.isArray(formData.Q19)) {
						const selectedQuestions = formData.Q19.map(
							(i) =>
								questionSchema.RELATIONSHIP_WITH_YOUR_DOG.Q19.fields[
									Number.parseInt(i)
								],
						);
						return selectedQuestions;
					}
					const likertScales =
						questionSchema.RELATIONSHIP_WITH_YOUR_DOG.Q19.fields;

					const keyQuestions = likertScales.filter(
						(likertScale) => likertScale.isKeyQuestion,
					);
					const optionalQuestions = likertScales.filter(
						(likertScale) => !likertScale.isKeyQuestion,
					);

					const randomOptionalQuestions = optionalQuestions
						.sort(() => 0.5 - Math.random())
						.slice(0, 1);

					const selectedQuestions = [
						...keyQuestions,
						...randomOptionalQuestions,
					];
					const indexOfSelectedQuestions = selectedQuestions.map((question) =>
						likertScales.indexOf(question),
					);

					setFormData((prevFormData) => ({
						...prevFormData,
						Q19: indexOfSelectedQuestions.map((i) => `${i}`),
					}));

					return [...keyQuestions, ...randomOptionalQuestions];
				}

				// Show all key questions then 2 random non key
				case "Q20": {
					if (formData.Q20 && Array.isArray(formData.Q20)) {
						const selectedQuestions = formData.Q20.map(
							(i) =>
								questionSchema.RELATIONSHIP_WITH_YOUR_DOG.Q20.fields[
									Number.parseInt(i)
								],
						);
						return selectedQuestions;
					}
					const likertScales =
						questionSchema.RELATIONSHIP_WITH_YOUR_DOG.Q20.fields;

					const keyQuestions = likertScales.filter(
						(likertScale) => likertScale.isKeyQuestion,
					);
					const optionalQuestions = likertScales.filter(
						(likertScale) => !likertScale.isKeyQuestion,
					);

					const randomOptionalQuestions = optionalQuestions
						.sort(() => 0.5 - Math.random())
						.slice(0, 2);

					const selectedQuestions = [
						...keyQuestions,
						...randomOptionalQuestions,
					];
					const indexOfSelectedQuestions = selectedQuestions.map((question) =>
						likertScales.indexOf(question),
					);

					setFormData((prevFormData) => ({
						...prevFormData,
						Q20: indexOfSelectedQuestions.map((i) => `${i}`),
					}));

					return [...keyQuestions, ...randomOptionalQuestions];
				}

				// All questions, Completely random order
				case "Q27": {
					if (formData.Q27 && Array.isArray(formData.Q27)) {
						const selectedQuestions = formData.Q27.map(
							(i) => questionSchema.ABOUT_YOU.Q27.fields[Number.parseInt(i)],
						);
						return selectedQuestions;
					}

					const likertScales = questionSchema.ABOUT_YOU.Q27.fields.slice(0, -1);

					const randomQuestions = likertScales.sort(() => 0.5 - Math.random());

					const indexOfSelectedQuestions = randomQuestions.map((question) =>
						likertScales.indexOf(question),
					);

					setFormData((prevFormData) => ({
						...prevFormData,
						Q27: indexOfSelectedQuestions.map((i) => `${i}`),
					}));
					return randomQuestions;
				}
				// Show all key questions then 6 random non key
				case "E4_1": {
					if (formData.E4_1 && Array.isArray(formData.E4_1)) {
						const selectedQuestions = formData.E4_1.map(
							(i) =>
								questionSchema.THOUGHTS_ON_DOGS.E4_1.fields[Number.parseInt(i)],
						);
						return selectedQuestions;
					}
					const likertScales = questionSchema.THOUGHTS_ON_DOGS.E4_1.fields;

					const keyQuestions = likertScales.filter(
						(likertScale) => likertScale.isKeyQuestion,
					);
					const optionalQuestions = likertScales.filter(
						(likertScale) => !likertScale.isKeyQuestion,
					);

					const randomOptionalQuestions = optionalQuestions
						.sort(() => 0.5 - Math.random())
						.slice(0, 6);

					const selectedQuestions = [
						...keyQuestions,
						...randomOptionalQuestions,
					];
					const indexOfSelectedQuestions = selectedQuestions.map((question) =>
						likertScales.indexOf(question),
					);

					setFormData((prevFormData) => ({
						...prevFormData,
						E4_1: indexOfSelectedQuestions.map((i) => `${i}`),
					}));

					return [...keyQuestions, ...randomOptionalQuestions];
				}

				// All key questions and two from each category
				case "E6": {
					if (formData.E6 && Array.isArray(formData.E6)) {
						const selectedQuestions = formData.E6.map(
							(i) =>
								questionSchema.THOUGHTS_ON_DOGS.E6.fields[Number.parseInt(i)],
						);
						return selectedQuestions;
					}
					const likertScales = questionSchema.THOUGHTS_ON_DOGS.E6.fields;

					const keyQuestions = likertScales.filter(
						(likertScale) => likertScale.isKeyQuestion,
					);
					const optionalQuestions = likertScales.filter(
						(likertScale) => !likertScale.isKeyQuestion,
					);

					const categories = [
						...new Set(optionalQuestions.map((field) => field.category)),
					];
					const selectedQuestions = [...keyQuestions];
					for (const filterCategory of categories) {
						const categoryQuestions = optionalQuestions.filter(
							({ category }) => category === filterCategory,
						);

						const randomCategoryQuestions = categoryQuestions
							.sort(() => 0.5 - Math.random())
							.slice(0, 2);

						selectedQuestions.push(...randomCategoryQuestions);
					}

					const indexOfSelectedQuestions = selectedQuestions.map((question) =>
						likertScales.indexOf(question),
					);

					setFormData((prevFormData) => ({
						...prevFormData,
						E6: indexOfSelectedQuestions.map((i) => `${i}`),
					}));

					return selectedQuestions;
				}
			}
		},
		[
			questionSchema,
			hasInitialisedFromCookies,
			formData,
			formData.E4_1,
			formData.E6,
		],
	);

	const onSpecialNavigation = useCallback(
		async (nextRoute: NDS.ScreenIndex, nextDogIndex?: 1 | 2 | 3 | 4 | 5) => {
			const screenState =
				surveyState.routeStack[surveyState.routeStack.length - 1];

			// They were on a potential end screen but did not choose to submit.
			if (SOFT_END_SCREENS.includes(screenState.screen)) {
				await onSoftSubmit();
			}
			const nextScreenState =
				nextDogIndex !== undefined
					? {
							...screenState,
							...nextRoute,
						}
					: {
							...screenState,
							...nextRoute,
						};

			surveyState.routeStack.push(
				nextScreenState as unknown as NDS.ScreenState,
			);

			setSurveyState({ ...surveyState });

			scrollToTop();
		},
		[scrollToTop, surveyState, onSoftSubmit, setSurveyState],
	);

	useEffect(() => {
		const screenState =
			surveyState.routeStack[surveyState.routeStack.length - 1];
		if (screenState.screen === "THANK_YOU" && hasSubmitted) {
			resetSurvey();
		}
	}, [surveyState, hasSubmitted, resetSurvey]);

	const onLoadSupporterId = useCallback(
		(supporterId: string) => {
			setSurveyState(
				(prevSurveyState) =>
					({ ...prevSurveyState, supporterId }) as NDS.SurveyState,
			);
		},
		[setSurveyState],
	);

	// Prevents jumping to our National Dog Survey's landing page if completing the survey for a second time, third time, etc.
	// Relates to the following ticket: https://aerstudios.atlassian.net/browse/DT-4267
	useEffect(() => {
		try {
			if (
				screenState.screen === "THANK_YOU" ||
				window.location.pathname === "/national-dog-survey/global/nds-thank-you"
			) {
				return;
			}

			const thankYouStatus = localStorage.getItem(THANK_YOU_STATUS);

			if (!thankYouStatus) {
				return;
			}

			if (thankYouStatus === "viewed") {
				localStorage.removeItem(THANK_YOU_STATUS);
				return;
			}
		} catch (error) {
			console.error(
				"Error handling 'THANK_YOU_STATUS' in localStorage:",
				error,
			);
		}
	}, [screenState.screen]);

	const handleThankYouReload = useCallback(() => {
		resetSurvey();
		navigate("/about-us/what-we-do/national-dog-survey");
	}, [resetSurvey]);

	const getCurrentDogName = useCallback(() => {
		const dogNames = Object.keys(screenState.dogsState);
		for (const dogName of dogNames) {
			if (screenState.dogsState[dogName].isActive) {
				return dogName;
			}
		}

		return "your dog";
	}, [screenState.dogsState]);

	const updateFieldsSeen = useCallback(
		(name: NDS.FormFieldName) => {
			let fieldName = name;
			if (multiDogFields.includes(name)) {
				// Handle multi dog fields
				const dogIndex = Object.values(screenState.dogsState).find(
					(dog) => dog.isActive,
				)?.index as 1 | 2 | 3 | 4 | 5;

				if (!dogIndex || dogIndex < 1 || dogIndex > 5) {
					console.error("Dog index is out of range");
					return;
				}
				fieldName = `${name}${dogIndex}` as NDS.FormFieldName;
			}
			if (fieldsSeen.includes(fieldName)) return;
			setFieldsSeen([...fieldsSeen, fieldName]);
		},
		[fieldsSeen, screenState.dogsState],
	);

	return (
		<NationalDogSurveyContext.Provider
			value={{
				surveyState,
				setSurveyState,
				dogIndex: Object.values(screenState.dogsState).find(
					(dog) => dog.isActive,
				)?.index as 1 | 2 | 3 | 4 | 5,
				routeStack: surveyState.routeStack,
				setDisableNext,
				setFormData,
				setTransition,
				isConfirmMode,
				setIsConfirmMode,
				screenState,
				disableNext: disableNext || isLoading || hasSubmitted,
				formData,
				onBack,
				onNext,
				handleChange,
				currentDogName: getCurrentDogName(),
				onSpecialNavigation,
				onSubmit,
				onSoftSubmit,
				onLoadWarmRoute,

				transition,
				clearSurveyCookies,
				importSurveyCookies,
				exportSurveyCookies,
				hasInitialisedFromCookies,
				questionSchema,
				setQuestionSchema,
				onLoadSupporterId,
				handleThankYouReload,
				isLoading,
				getFieldsForQuestion,
				updateFieldsSeen,
				// For debugging
				resetSurvey,
				restartWarmRouteDebug,
				restartSpecificRouteDebug,
				recentFieldChange,
			}}
		>
			{children}
		</NationalDogSurveyContext.Provider>
	);
};
export const useNationalDogSurveyContext = (): NationalDogSurveyContextState =>
	useContext(NationalDogSurveyContext);
